import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Loader2, Maximize2, Layers, ShieldAlert } from "lucide-react";

const RARITY_COLORS = { exclusive: "#F87171", icon: "#FFFFFF", legendary: "#C4B5FD", elite: "#FCD34D", rare: "#38BDF8", common: "#A8A29E", gem: "#38BDF8", gold: "#EAB308", holo: "#E879F9", standard: "#A1A1AA" };

function CardThumb({ c, onExpand, children }) {
  const col = RARITY_COLORS[c.rarity] || RARITY_COLORS.standard;
  return (
    <div className="border border-pb-border bg-pb-elevated/40 p-2 flex flex-col gap-1.5" data-testid={`pool-card-${c.id}`}>
      <button className="relative group" onClick={() => onExpand(c)} data-testid={`pool-expand-${c.id}`} title="Expand">
        <img src={c.url} alt={c.name} className="w-full aspect-[3/4] object-contain bg-black" loading="lazy" />
        <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/50">
          <Maximize2 className="w-6 h-6 text-white" />
        </span>
        {c.bounty_prize > 0 && <span className="absolute top-1 right-1 bg-black/80 text-pb-red text-[8px] font-black px-1.5 py-0.5">BOUNTY {c.bounty_prize}</span>}
        {c.one_of_one && <span className="absolute top-1 left-1 bg-black/80 text-amber-400 text-[8px] font-black px-1.5 py-0.5">1/1</span>}
      </button>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] font-semibold truncate">{c.name}</span>
        <span className="shrink-0 text-[7px] font-black uppercase tracking-widest px-1 py-0.5 border"
          style={{ color: col, borderColor: `${col}66` }}>
          {c.gem_label ? c.gem_label.split(" ")[0] : c.bullpen_exclusive ? "EXCL" : c.rarity}
        </span>
      </div>
      {children}
    </div>
  );
}

function ClassifyRow({ c, onDone }) {
  const [busy, setBusy] = useState(false);
  const set = async (patch) => {
    setBusy(true);
    try {
      await api.post(`/admin/cards/${c.id}/classify`, patch);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const btn = "flex-1 text-[8px] font-black uppercase tracking-widest py-1 border transition-colors disabled:opacity-50";
  return (
    <div className="flex gap-1" data-testid={`classify-${c.id}`}>
      <button data-testid={`classify-male-${c.id}`} disabled={busy} title="Tag as Male (tap again to clear)"
        onClick={() => set({ gender: c.gender === "male" ? "" : "male" })}
        className={`${btn} ${c.gender === "male" ? "border-sky-400 text-sky-300 bg-sky-400/10" : "border-pb-border text-zinc-600 hover:border-sky-400/60"}`}>♂</button>
      <button data-testid={`classify-female-${c.id}`} disabled={busy} title="Tag as Female (tap again to clear)"
        onClick={() => set({ gender: c.gender === "female" ? "" : "female" })}
        className={`${btn} ${c.gender === "female" ? "border-pink-400 text-pink-300 bg-pink-400/10" : "border-pb-border text-zinc-600 hover:border-pink-400/60"}`}>♀</button>
      <button data-testid={`classify-singles-${c.id}`} disabled={busy} title="Singles Specialist (tap again to clear)"
        onClick={() => set({ specialist: c.specialist === "singles" ? "" : "singles" })}
        className={`${btn} ${c.specialist === "singles" ? "border-cyan-400 text-cyan-300 bg-cyan-400/10" : "border-pb-border text-zinc-600 hover:border-cyan-400/60"}`}>SGL</button>
      <button data-testid={`classify-doubles-${c.id}`} disabled={busy} title="Doubles Specialist (tap again to clear)"
        onClick={() => set({ specialist: c.specialist === "doubles" ? "" : "doubles" })}
        className={`${btn} ${c.specialist === "doubles" ? "border-violet-400 text-violet-300 bg-violet-400/10" : "border-pb-border text-zinc-600 hover:border-violet-400/60"}`}>DBL</button>
    </div>
  );
}

export default function PoolApprovalPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showLive, setShowLive] = useState(false);

  const load = () => { api.get("/admin/pool/cards").then(({ data: d }) => setData(d)).catch(() => {}); };
  useEffect(load, []);

  const decide = async (c, approve) => {
    setBusy(c.id);
    try {
      await api.post(`/admin/pool/cards/${c.id}/decide`, { approve });
      toast.success(approve ? `"${c.name}" approved — now pullable in packs` : `"${c.name}" kept OUT of the pack pool`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="pool-approval-panel">
      <p className="text-xs text-zinc-500">
        Every minted card — player or admin — goes LIVE in the pack pool instantly. This panel is your moderation desk:
        click any card to expand it full size, and "Pull from pool" removes a card from packs (it stays in the owner's
        personal collection). Restore a denied card any time. The ♂ / ♀ / SGL / DBL buttons under each card set its
        Card Wars gender and Singles/Doubles Specialist classification.
      </p>

      {data.pending.length > 0 && (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-black">Awaiting approval ({data.pending.length})</span>
        </div>
        {data.pending.length === 0 ? (
          <div className="border border-dashed border-pb-border p-5 text-xs text-zinc-600" data-testid="pool-pending-empty">
            No cards waiting — new player cards will appear here for review.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pool-pending-grid">
            {data.pending.map((c) => (
              <CardThumb key={c.id} c={c} onExpand={setExpanded}>
                <ClassifyRow c={c} onDone={load} />
                <div className="flex gap-1">
                  <button data-testid={`pool-approve-${c.id}`} onClick={() => decide(c, true)} disabled={!!busy}
                    className="flex-1 flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-black py-1.5 hover:bg-[#BEF264] transition-colors disabled:opacity-50">
                    {busy === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                  </button>
                  <button data-testid={`pool-deny-${c.id}`} onClick={() => decide(c, false)} disabled={!!busy}
                    className="flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-widest border border-red-500/60 text-red-400 px-2 py-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </CardThumb>
            ))}
          </div>
        )}
      </div>
      )}

      <div>
        <button data-testid="pool-live-toggle" onClick={() => setShowLive(!showLive)}
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-black hover:text-white transition-colors">
          <Layers className="w-3.5 h-3.5 text-lime-400" /> Live in the pack pool ({data.live.length}) {showLive ? "▾" : "▸"}
        </button>
        {showLive && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3" data-testid="pool-live-grid">
            {data.live.map((c) => (
              <CardThumb key={c.id} c={c} onExpand={setExpanded}>
                <ClassifyRow c={c} onDone={load} />
                <button data-testid={`pool-pull-${c.id}`} onClick={() => decide(c, false)} disabled={!!busy}
                  className="w-full text-[8px] font-black uppercase tracking-widest border border-pb-border text-zinc-500 py-1 hover:border-red-500/60 hover:text-red-400 transition-colors disabled:opacity-50">
                  Pull from pool
                </button>
              </CardThumb>
            ))}
          </div>
        )}
      </div>

      {data.denied.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-black mb-2">Denied / pulled ({data.denied.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="pool-denied-grid">
            {data.denied.map((c) => (
              <div key={c.id} className="relative border border-pb-border/50 opacity-60">
                <button onClick={() => setExpanded(c)} className="block w-full"><img src={c.url} alt="" className="w-full aspect-[3/4] object-contain bg-black grayscale" /></button>
                <button data-testid={`pool-restore-${c.id}`} onClick={() => decide(c, true)} disabled={!!busy}
                  className="absolute bottom-0 inset-x-0 text-[8px] font-black uppercase bg-black/80 text-emerald-400 py-1 hover:bg-emerald-500 hover:text-black transition-colors">
                  Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {expanded && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setExpanded(null)} data-testid="pool-lightbox">
          <div className="max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img src={expanded.url} alt={expanded.name} className="max-h-[80vh] max-w-full object-contain border border-zinc-700" />
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold">{expanded.name}</span>
              <button data-testid="pool-lightbox-close" onClick={() => setExpanded(null)}
                className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest border border-zinc-600 px-3 py-1.5 hover:border-white transition-colors">
                <X className="w-3 h-3" /> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
